import React from 'react';
import { Button, Input, Loader, TextArea, useToasts } from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { pick, uniq } from 'lodash';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { ContactListType } from '../../../../shared/types';
import {
  Center,
  FormActions,
  FormContainer,
  FormContent,
  FormField,
  FormHeader,
} from '../../../../shared/styles';
import {
  SaveContactListType,
  useGetContactList,
  useSaveContactList,
} from '../../../screens/lists/hooks';

type NewListFormProps = {
  setIsDirty: (isDirty: boolean) => void;
  toggleDrawer: () => void;
  isFetching?: boolean;
  loaderText?: string;
  loaderSubText?: string;
  contactLists: ContactListType[];
  values?: ContactListType & {
    clone?: boolean;
  };
};

const NewListForm = (props: NewListFormProps) => {
  const { t } = useTranslation();
  const {
    toggleDrawer,
    isFetching = false,
    loaderText,
    contactLists,
    setIsDirty,
    loaderSubText,
    values = {},
  } = props;
  const [, setToast] = useToasts();
  const [takenNames, setTakenNames] = React.useState<string[]>([]);

  const {
    data: contactList = {},
    isLoading: isLoadingContactList,
    isError: isContactListError,
  } = useGetContactList({
    contactListId: values.clone ? '' : values.display_id || '',
  });

  const { mutate: saveContactList, isLoading: isSavingContactList } =
    useSaveContactList({
      setToast,
      callback: (
        isSuccess: boolean,
        response: any,
        variables: SaveContactListType,
      ) => {
        if (isSuccess) {
          toggleDrawer();
        } else if (
          response?.response?.data === 'ContactList name is not unique'
        ) {
          setTakenNames((old) =>
            uniq([...old, variables.payload.label || '']).filter((v) => !!v),
          );
          form.setError('label', {
            message: `${t('forms.toast.error.contact_list_exists', {
              label: variables.payload.label,
            })}`,
          });
        }
      },
    });

  const form = useForm<ContactListType>({
    resolver: (formValues: ContactListType, ...resolverProps) => {
      return yupResolver(
        Yup.object({
          created_by_id: Yup.string().nullable(true),
          display_id: Yup.string().nullable(true),
          label: Yup.string()
            .required(`${t('forms.err_msg.list_name_required')}`)
            .trim()
            .max(
              50,
              `${t('forms.err_msg.max_char', {
                len: '50',
              })}`,
            )
            .min(
              3,
              `${t('forms.err_msg.min_char', {
                len: '3',
              })}`,
            )
            .test({
              message: 'Error',
              name: 'name',
              test: function (name, context) {
                const { createError } = context || {};
                const currentTakenNames = !values.display_id
                  ? takenNames
                  : takenNames.filter((n) => n !== values.label);
                if (currentTakenNames.includes(name || '')) {
                  return createError({
                    message: `${t('forms.toast.error.contact_list_exists', {
                      label: name,
                    })}`,
                  });
                }
                return true;
              },
            }),
          description: Yup.string()
            .nullable(true)
            .max(
              256,
              `${t('forms.err_msg.max_char', {
                len: '256',
              })}`,
            ),
        }).required(),
      )(formValues, ...resolverProps);
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, submitCount, errors },
  } = form;

  const onSubmit = (data: ContactListType) => {
    const isClone = !!values?.clone;
    saveContactList({
      payload: isClone
        ? { ...data, display_id: values?.display_id || '' }
        : data,
      clone: isClone,
      name: data?.label || '',
    });
  };

  React.useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty]);

  React.useEffect(() => {
    if (contactList.display_id) reset({ ...contactList });
  }, [contactList]);

  React.useEffect(() => {
    const newNames = (contactLists || [])
      .map((contactList) => contactList.label || '')
      .filter((v) => !!v);
    setTakenNames((old) => uniq([...old, ...newNames]));
  }, [contactLists]);

  React.useEffect(() => {
    if (values.clone) {
      reset(
        {
          ...pick(values, ['description', 'label', 'display_id']),
        },
        {
          keepDefaultValues: true,
        },
      );
      return;
    }
    if (!contactList.display_id) {
      reset({});
    }
  }, []);

  if (isLoadingContactList || isFetching)
    return (
      <Center>
        <Loader />
        {loaderText && <div className="text">{loaderText}</div>}
        {loaderSubText && <div className="sub-text">{loaderSubText}</div>}
      </Center>
    );

  if (isContactListError && values.display_id)
    return <Center>{t('empty_state.message.network_error')}</Center>;

  return (
    <FormContainer
      onSubmit={handleSubmit(onSubmit, (err) => console.log('ERR', err))}
    >
      <FormHeader hideBorder>
        <div className="heading">
          {`${values.display_id ? 'Edit' : 'New'} contact list`}
        </div>
      </FormHeader>
      <FormContent>
        <FormField>
          <Input
            {...register('label')}
            id="label"
            label={`${t('forms.labels.contact_list_name')}`}
            isRequired
            isErrored={submitCount > 0 && !!errors?.label?.message}
            errorText={errors?.label?.message}
          />
        </FormField>
        <FormField>
          <TextArea
            {...register('description')}
            id="description"
            label={`${t('description')}`}
            rows={4}
            isErrored={submitCount > 0 && !!errors?.description?.message}
            errorText={errors?.description?.message}
          />
        </FormField>
      </FormContent>
      <FormActions>
        <div />
        <div>
          <Button
            inline
            htmlType="button"
            type="secondary"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              toggleDrawer();
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            inline
            htmlType="submit"
            disabled={!isDirty || isSavingContactList}
          >
            {t('save')}
          </Button>
        </div>
      </FormActions>
    </FormContainer>
  );
};

export default NewListForm;
